import { useId, useEffect, useRef } from "react";
import { B, space, font, radius, weight } from "../../constants/brand";
import { Icon } from "./Icon";
import { sanitizeNumericInput } from "../../utils/numericInput";
export { ToastProvider, useToast } from "./Toast";
export { GlobalStyles } from "./GlobalStyles";
export { Icon } from "./Icon";

// ── clickable() — keyboard support for a div that behaves as a button ────────
//
// A <div onClick> is invisible to the keyboard and to assistive tech: it takes
// no focus and Enter/Space do nothing, so a control built that way can only be
// operated with a mouse. Spread this onto one and it becomes a real button as
// far as the browser is concerned.
//
//   <div {...clickable(() => open(row))} style={...}>
//
// TWO CASES DELIBERATELY DO NOT WANT THIS, and adding it to them makes things
// worse rather than better:
//   * `onClick={e => e.stopPropagation()}` wrappers — they are not controls,
//     they are containment for the real buttons inside them. Making one focusable
//     inserts a tab stop that does nothing.
//   * full-screen backdrops that dismiss a menu or drawer on an outside click —
//     a focusable overlay traps the keyboard, and the keyboard equivalent of
//     "click outside" is Escape, not Tab-to-the-backdrop.
//
// Where several cells in one row all fire the same handler, put this on the ROW
// once. One control is one tab stop; four cells that open the same task are four
// tab stops that all do the same thing.
export const clickable = (onActivate, { disabled = false, role = "button" } = {}) => ({
  role,
  tabIndex: disabled ? -1 : 0,
  "aria-disabled": disabled || undefined,
  onClick: disabled ? undefined : onActivate,
  onKeyDown: disabled ? undefined : (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(e); }
  },
});

export const Badge = ({ children, color = B.navy }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    background: color + "16", color, border: `1px solid ${color}2e`,
    borderRadius: radius.pill, padding: "3px 10px", fontSize: font.xs, fontWeight: weight.semibold,
    letterSpacing: 0.2, whiteSpace: "nowrap", lineHeight: 1.4,
  }}>{children}</span>
);

// STATUS_COLORS — single source of truth for status → color across the app.
const STATUS_COLORS = {
  Active: B.green, Current: B.green, Succeeded: B.green, Collected: B.green, Paid: B.green, Exited: B.tealDk, "Exit'd": B.tealDk, Completed: B.tealDk, Converted: B.tealDk,
  Delinquent: B.red, Dead: B.red, Failed: B.red, Default: B.red, Inactive: B.red, Cancelled: B.red,
  Pending: B.amber, Initial: B.amber, Running: B.amber, ReminderSent: B.amber, Upcoming: B.amber, UnderContract: B.amber,
  "Due Diligence": B.navy, LOI: B.navy,
  Expired: B.textMd, Withdrawn: B.textMd,
  Low: B.tealDk, Medium: B.amber, High: B.red, Critical: "#7c1a1a",
  Fixed: B.navy, Variable: B.amber,
  HFS: B.navyLt, Investment: B.tealDk,
  Sold: B.tealDk,
};

// statusColor — raw color for a status, for callers that need a color (not a Badge).
export const statusColor = (status) => STATUS_COLORS[status] || B.textMd;

export const StatusBadge = ({ status }) => (
  <Badge color={statusColor(status)}>{status}</Badge>
);

export const Card = ({ children, style = {}, interactive = false, ...rest }) => (
  <div {...rest}
    style={{
      background: B.surface, borderRadius: radius.xxxl, border: `1px solid ${B.border}`,
      boxShadow: B.shadowSm, padding: space.xxl,
      transition: interactive ? "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease" : undefined,
      ...style,
    }}
    onMouseEnter={interactive ? (e) => { e.currentTarget.style.boxShadow = B.shadowLift; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = B.tealDk; } : undefined}
    onMouseLeave={interactive ? (e) => { e.currentTarget.style.boxShadow = B.shadowSm; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = B.border; } : undefined}
  >{children}</div>
);

export const KpiCard = ({ label, value, sub, color = B.navy }) => (
  <div style={{ background: B.surface, borderRadius: radius.xxxl, border: `1px solid ${B.border}`, boxShadow: B.shadowSm, padding: "20px 22px", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: color }} />
    <div style={{ fontSize: font.xs, color: B.textMd, fontWeight: weight.semibold, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: space.sm }}>{label}</div>
    <div style={{ fontSize: font.xxl, fontWeight: weight.bold, color: B.text, lineHeight: 1, letterSpacing: -0.5 }}>{value}</div>
    {sub && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 6 }}>{sub}</div>}
  </div>
);

// Inline spinner glyph used by Btn loading state.
const InlineSpinner = ({ color }) => (
  <span aria-hidden="true" style={{
    width: 12, height: 12, borderRadius: "50%",
    border: `2px solid ${color}55`, borderTopColor: color,
    display: "inline-block", animation: "harvus-spin 0.6s linear infinite",
  }} />
);

// Per-variant base + hover backgrounds. Hover is a real color shift (not just
// opacity), giving buttons a tactile, modern feel.
// Per-variant base + hover backgrounds. Primary/danger carry a soft gradient
// sheen + shadow for a tactile, modern (Fluent-ish) feel; hover is a real color
// shift, and controls press in slightly on click.
const BTN_VARIANTS = {
  primary:   { base: { background: B.navy,        color: B.white, boxShadow: B.shadowSm, backgroundImage: B.gradPrimary }, hover: B.navyDk },
  secondary: { base: { background: B.tealLt,      color: B.navyDk },                       hover: B.teal   },
  ghost:     { base: { background: B.surface,     color: B.navy, border: `1px solid ${B.border}`, boxShadow: B.shadowXs }, hover: B.surfaceMuted },
  danger:    { base: { background: B.red,         color: B.white, boxShadow: B.shadowSm },  hover: "#9f2f22" },
};

// `hoverBackground` overrides the variant's hover fill; a `style.background`
// is kept on mouse-out (it used to be reset to the variant's) — both so a
// caller can recolour a button (the Lodge's per-outfitter brand colours).
export const Btn = ({ children, onClick, variant = "primary", size = "md", style = {}, disabled = false, loading = false, hoverBackground, ...rest }) => {
  const sizes = { sm: { padding: "7px 14px", fontSize: font.sm }, md: { padding: "10px 18px", fontSize: font.base }, lg: { padding: "13px 24px", fontSize: font.md } };
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  const isDisabled = disabled || loading;
  const spinnerColor = style.color || (variant === "secondary" || variant === "ghost" ? B.navy : B.white);
  const baseBg = style.background || v.base.background;
  const baseImg = style.backgroundImage || v.base.backgroundImage || "none";
  const hoverBg = hoverBackground || v.hover;
  return (
    <button onClick={isDisabled ? undefined : onClick} disabled={isDisabled} aria-busy={loading || undefined} {...rest} style={{
      border: "none", borderRadius: radius.xl2, cursor: isDisabled ? "not-allowed" : "pointer",
      fontFamily: "inherit", fontWeight: weight.semibold, transition: "background 0.15s, box-shadow 0.15s, opacity 0.15s, transform 0.08s",
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: space.xs + 2,
      opacity: isDisabled ? 0.5 : 1, whiteSpace: "nowrap", letterSpacing: 0.1,
      ...sizes[size], ...v.base, ...style,
    }}
      onMouseEnter={e => { if (!isDisabled) { e.currentTarget.style.background = hoverBg; e.currentTarget.style.backgroundImage = "none"; } }}
      onMouseLeave={e => { e.currentTarget.style.background = baseBg; e.currentTarget.style.backgroundImage = baseImg; e.currentTarget.style.transform = "translateY(0)"; }}
      onMouseDown={e => { if (!isDisabled) e.currentTarget.style.transform = "translateY(1px)"; }}
      onMouseUp={e => { if (!isDisabled) e.currentTarget.style.transform = "translateY(0)"; }}>
      {loading && <InlineSpinner color={spinnerColor} />}
      {children}
    </button>
  );
};

// Shared focus behavior for text controls — accent border + soft cool ring.
export const focusHandlers = (error) => ({
  onFocus: (e) => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; },
  onBlur:  (e) => { e.currentTarget.style.borderColor = error ? B.red : B.border; e.currentTarget.style.boxShadow = "none"; },
});

export const fieldStyle = (error, disabled, extra = {}) => ({
  width: "100%", padding: "10px 13px", border: `1px solid ${error ? B.red : B.border}`,
  borderRadius: radius.xl, fontSize: font.base, color: B.text,
  background: disabled ? B.surfaceMuted : B.surface, outline: "none", boxSizing: "border-box",
  fontFamily: "inherit", transition: "border-color 0.15s, box-shadow 0.15s", ...extra,
});

const labelStyle = { display: "block", fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.xs, letterSpacing: 0.3 };
export const FieldLabel = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} style={labelStyle}>{children}{required && <span style={{ color: B.red }}> *</span>}</label>
);

// A NUMBER FIELD IS RENDERED AS TEXT, DELIBERATELY — see utils/numericInput.js
// for the measurements. `<input type="number">` empties itself for anything its
// parser rejects, so the component receives "" while the characters stay on
// screen; in Chromium 141 that is a lone "." or "-" (badInput), and a trailing
// "1." silently becomes "1". Which characters survive is a property of the
// engine, not of the spec, and this app is used from an iPad as well as a
// desktop — so a field is rendered as text and the raw string is passed through
// on every engine, rather than validated from contents that depend on who is
// rendering them.
//
// WHAT THIS IS NOT: it was written chasing a cap rate typed as ".08" that would
// not save, and a real-browser check afterwards showed ".08" survives a number
// input under React — so this is NOT the confirmed cause of that report. It is
// worth keeping on its own terms; it should not be cited as that fix.
//
// text + inputMode="decimal" keeps the numeric keypad on a phone or iPad;
// sanitizeNumericInput is what replaces type=number's own refusal of letters.
// Callers are unaffected — `type="number"` handed them a string before and
// still does.
// ariaLabel: the accessible name for an input shown without a visible label
// (one row of a list under a shared heading). Ignored when `label` is set.
export const Input = ({ label, value, onChange, type = "text", placeholder = "", required = false, disabled = false, onKeyDown, autoFocus, error, ariaLabel }) => {
  const id = useId();
  const errId = `${id}-err`;
  const numeric = type === "number";
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>}
      <input id={id} type={numeric ? "text" : type} inputMode={numeric ? "decimal" : undefined}
        value={type === 'date' && value ? String(value).slice(0, 10) : (value ?? "")}
        onChange={e => onChange(numeric ? sanitizeNumericInput(e.target.value) : e.target.value)}
        placeholder={placeholder} disabled={disabled}
        onKeyDown={onKeyDown} autoFocus={autoFocus} required={required}
        aria-invalid={error ? "true" : undefined} aria-describedby={error ? errId : undefined}
        aria-label={!label && ariaLabel ? ariaLabel : undefined}
        {...focusHandlers(error)}
        style={fieldStyle(error, disabled, { cursor: disabled ? "not-allowed" : "text" })} />
      {error && <div id={errId} style={{ fontSize: font.xs, color: B.red, marginTop: space.xs }}>{error}</div>}
    </div>
  );
};

export const Textarea = ({ label, value, onChange, placeholder = "", rows = 3, required = false, disabled = false, error }) => {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>}
      <textarea id={id} value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled} required={required}
        aria-invalid={error ? "true" : undefined} aria-describedby={error ? errId : undefined}
        {...focusHandlers(error)}
        style={fieldStyle(error, disabled, { resize: "vertical", cursor: disabled ? "not-allowed" : "text" })} />
      {error && <div id={errId} style={{ fontSize: font.xs, color: B.red, marginTop: space.xs }}>{error}</div>}
    </div>
  );
};

export const Select = ({ label, value, onChange, options, disabled = false, required = false, error }) => {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>}
      <select id={id} value={value ?? ""} onChange={e => onChange(e.target.value)} disabled={disabled} required={required}
        aria-invalid={error ? "true" : undefined} aria-describedby={error ? errId : undefined}
        {...focusHandlers(error)}
        style={fieldStyle(error, disabled, { cursor: disabled ? "not-allowed" : "pointer", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234a6572' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 36 })}>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
      {error && <div id={errId} style={{ fontSize: font.xs, color: B.red, marginTop: space.xs }}>{error}</div>}
    </div>
  );
};

// Shared overlay/dialog style helpers — so hand-rolled modals can converge on
// the same backdrop + container treatment as <Modal>.
export const overlayStyle = { position: "fixed", inset: 0, background: B.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: space.xl, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", animation: "harvus-fade-in 0.15s ease-out" };
export const dialogStyle = (width = 560) => ({ background: B.surface, borderRadius: radius.xxxl, width: "100%", maxWidth: `min(${width}px, calc(100vw - 32px))`, maxHeight: "90vh", overflowY: "auto", boxShadow: B.shadowXl, outline: "none", animation: "harvus-pop-in 0.18s cubic-bezier(0.22, 1, 0.36, 1)" });

// ModalFooter — the common right-aligned action row.
export const ModalFooter = ({ children }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: space.sm + 2, marginTop: space.xl }}>{children}</div>
);

export const Modal = ({ title, onClose, children, width = 560, headerAction }) => {
  const dialogRef = useRef(null);
  const titleId = useId();
  // Keep onClose in a ref so the mount-only effect always calls the latest
  // handler without re-running (which would steal focus back to the first
  // field on every parent re-render — e.g. while the user is typing).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prevFocused = document.activeElement;
    const node = dialogRef.current;

    // Focus the first focusable element (or the dialog itself) on open.
    const focusables = () => node
      ? node.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      : [];
    const first = focusables()[0];
    (first || node)?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onCloseRef.current?.(); return; }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) { e.preventDefault(); return; }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    node?.addEventListener("keydown", onKeyDown);
    return () => {
      node?.removeEventListener("keydown", onKeyDown);
      // Restore focus to whatever triggered the modal.
      if (prevFocused && typeof prevFocused.focus === "function") prevFocused.focus();
    };
  }, []);

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        style={dialogStyle(width)} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${B.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: B.surface, zIndex: 1, borderTopLeftRadius: radius.xxxl, borderTopRightRadius: radius.xxxl }}>
          <div id={titleId} style={{ fontWeight: weight.bold, fontSize: font.lg, color: B.text, letterSpacing: -0.2 }}>{title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: space.md }}>
            {headerAction}
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: B.textMd, lineHeight: 1, display: "flex", padding: 4, borderRadius: radius.sm }}
              onMouseEnter={e => { e.currentTarget.style.background = B.surfaceMuted; e.currentTarget.style.color = B.text; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = B.textMd; }}>
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>
        <div style={{ padding: space.xxl }}>{children}</div>
      </div>
    </div>
  );
};

// `virtualize` — for long lists, let the browser skip layout/paint of off-screen
// rows via CSS content-visibility. `contain-intrinsic-size: auto <estimate>`
// reserves space (so the scrollbar is right and there's no jump) and, once a row
// has been rendered once, its real measured size is remembered. Unlike JS row
// windowing this keeps every row in the DOM (so find-in-page, clicks and keyboard
// nav all still work) and tolerates variable row heights — it only skips the
// expensive layout/paint work for rows the user can't see. Opt-in so existing
// small tables render exactly as before.
export const Table = ({ columns, data, onRowClick, rowFilter, virtualize = false }) => {
  const rows = rowFilter ? (data || []).filter(rowFilter) : (data || []);
  const cv = virtualize ? { contentVisibility: "auto", containIntrinsicSize: "auto 46px" } : null;
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${B.border}`, borderRadius: radius.xl2, background: B.surface, boxShadow: B.shadowXs }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: font.base }}>
        <thead>
          <tr style={{ background: B.surfaceMuted }}>
            {columns.map(c => (
              <th key={c.key} scope="col" style={{ padding: "11px 14px", textAlign: c.align || "left", fontWeight: weight.semibold, color: B.textMd, fontSize: font.xs, letterSpacing: 0.5, textTransform: "uppercase", borderBottom: `1px solid ${B.border}`, whiteSpace: "nowrap" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id ?? row.loanID ?? row.dealID ?? i} onClick={() => onRowClick && onRowClick(row)}
              role={onRowClick ? "button" : undefined} tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); } }) : undefined}
              style={{ borderBottom: `1px solid ${B.divider}`, cursor: onRowClick ? "pointer" : "default", transition: "background 0.1s", ...cv }}
              onMouseEnter={e => e.currentTarget.style.background = B.hover}
              onMouseLeave={e => e.currentTarget.style.background = ""}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: "12px 14px", color: B.text, verticalAlign: "middle", textAlign: c.align || "left" }}>
                  {c.render ? c.render(row[c.key], row, i) : (row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} style={{ padding: "40px 14px", textAlign: "center", color: B.textMd }}>No records found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const Tabs = ({ tabs, active, onChange }) => {
  const onKeyDown = (e) => {
    const idx = tabs.indexOf(active);
    let next = null;
    if (e.key === "ArrowRight") next = tabs[(idx + 1) % tabs.length];
    else if (e.key === "ArrowLeft") next = tabs[(idx - 1 + tabs.length) % tabs.length];
    else if (e.key === "Home") next = tabs[0];
    else if (e.key === "End") next = tabs[tabs.length - 1];
    if (next != null) { e.preventDefault(); onChange(next); }
  };
  // Segmented / pill control (Outlook-iOS style): a muted track with the active
  // tab lifted as a white pill. Reads as a modern control instead of an underline.
  return (
    <div role="tablist" onKeyDown={onKeyDown} style={{ display: "inline-flex", gap: 3, background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.pill, padding: 4, marginBottom: space.xl, maxWidth: "100%", overflowX: "auto", verticalAlign: "top" }}>
      {tabs.map(t => {
        const on = active === t;
        return (
          <button key={t} role="tab" aria-selected={on} tabIndex={on ? 0 : -1} onClick={() => onChange(t)} style={{
            padding: "7px 16px", border: "none", cursor: "pointer", fontFamily: "inherit", borderRadius: radius.pill,
            fontSize: font.base, fontWeight: on ? weight.semibold : weight.medium, color: on ? B.navyDk : B.textMd,
            background: on ? B.surface : "transparent", boxShadow: on ? B.shadowXs : "none",
            transition: "color 0.15s, background 0.15s, box-shadow 0.15s", whiteSpace: "nowrap",
          }}
            onMouseEnter={e => { if (!on) { e.currentTarget.style.color = B.text; e.currentTarget.style.background = B.hover; } }}
            onMouseLeave={e => { if (!on) { e.currentTarget.style.color = B.textMd; e.currentTarget.style.background = "transparent"; } }}>{t}</button>
        );
      })}
    </div>
  );
};

export const InfoGrid = ({ items, cols = 2 }) => (
  <div className="harvus-responsive-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: space.md, marginBottom: space.xl }}>
    {items.filter(Boolean).map(([label, value]) => (
      <div key={label} style={{ background: B.surfaceMuted, borderRadius: radius.md, padding: "10px 14px", border: `1px solid ${B.divider}` }}>
        <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: 3, fontWeight: weight.semibold, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>{value ?? "—"}</div>
      </div>
    ))}
  </div>
);

export const SectionHeader = ({ title, action }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space.md, marginTop: space.sm }}>
    <div style={{ fontWeight: weight.bold, fontSize: font.md, color: B.text }}>{title}</div>
    {action}
  </div>
);

// PageHeader — the standard page title row (title + optional subtitle + actions).
export const PageHeader = ({ title, subtitle, actions, icon }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: space.lg, marginBottom: space.xl, flexWrap: "wrap" }}>
    <div style={{ display: "flex", alignItems: "center", gap: space.md, minWidth: 0 }}>
      {icon && <span style={{ display: "flex", color: B.white, background: B.navy, backgroundImage: B.gradBrand, borderRadius: radius.xl2, padding: 10, boxShadow: B.shadowSm }}><Icon name={icon} size={20} /></span>}
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontWeight: weight.bold, fontSize: 22, color: B.text, letterSpacing: -0.5, lineHeight: 1.2, margin: 0 }}>{title}</h1>
        {subtitle && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 3 }}>{subtitle}</div>}
      </div>
    </div>
    {actions && <div style={{ display: "flex", alignItems: "center", gap: space.sm, flexShrink: 0 }}>{actions}</div>}
  </div>
);

export const ProgressBar = ({ value, max, color = B.tealDk, label }) => {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div role="progressbar" aria-valuenow={Math.round(value ?? 0)} aria-valuemin={0} aria-valuemax={max ?? 100} aria-label={label || "Progress"}
      style={{ background: B.surfaceSunken, borderRadius: radius.pill, height: 6, overflow: "hidden", flexGrow: 1, minWidth: 60 }}>
      <div style={{ background: color, height: "100%", width: `${pct}%`, borderRadius: radius.pill, transition: "width 0.3s" }} />
    </div>
  );
};

export const Spinner = ({ label = "Loading…" }) => (
  <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: space.md, padding: space.xxxl, color: B.textMd }}>
    <span aria-hidden="true" style={{
      width: 28, height: 28, borderRadius: "50%",
      border: `3px solid ${B.border}`, borderTopColor: B.navy,
      animation: "harvus-spin 0.7s linear infinite",
    }} />
    <span style={{ fontSize: font.base }}>{label}</span>
  </div>
);

export const ErrorMsg = ({ msg, onRetry }) => (
  <div role="alert" style={{ background: B.red + "0f", border: `1px solid ${B.red}33`, borderRadius: radius.lg, padding: space.lg, color: B.red, fontSize: font.base, display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.md, flexWrap: "wrap" }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: space.sm }}><Icon name="warning" size={16} />{msg}</span>
    {onRetry && <Btn variant="ghost" size="sm" onClick={onRetry} style={{ color: B.red, borderColor: `${B.red}55` }}>Retry</Btn>}
  </div>
);

export const InfoNote = ({ children }) => (
  <div style={{ background: B.tealLt, borderRadius: radius.lg, padding: "12px 16px", fontSize: font.sm, color: B.navyDk, marginBottom: space.lg, display: "flex", alignItems: "flex-start", gap: space.sm }}>
    <span style={{ color: B.navy, marginTop: 1 }}><Icon name="info" size={15} /></span>
    <span>{children}</span>
  </div>
);

// EmptyState — consistent "nothing here yet" block. Replaces the ad-hoc
// centered-muted-text patterns scattered across pages.
export const EmptyState = ({ icon = "info", title, message, action }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: `${space.xxxl}px ${space.xl}px`, color: B.textMd, gap: space.sm }}>
    <span style={{ display: "flex", color: B.tealDk, background: B.surfaceMuted, borderRadius: "50%", padding: 14, marginBottom: space.xs }}><Icon name={icon} size={26} strokeWidth={1.75} /></span>
    {title && <div style={{ fontSize: font.md, fontWeight: weight.semibold, color: B.text }}>{title}</div>}
    {message && <div style={{ fontSize: font.sm, color: B.textMd, maxWidth: 360 }}>{message}</div>}
    {action && <div style={{ marginTop: space.sm }}>{action}</div>}
  </div>
);

// Avatar — initials circle. Consolidates the several hand-rolled versions.
export const Avatar = ({ initials, name, size = 32, color = B.tealDk, textColor = B.white }) => {
  const chars = initials || (name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?");
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", background: color, color: textColor, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.4), fontWeight: weight.bold, flexShrink: 0, lineHeight: 1 }} aria-hidden="true">{chars}</span>
  );
};

// Chip — small removable/selectable pill. For selected filters, tags, people.
export const Chip = ({ children, color = B.navy, onRemove, onClick, style = {}, removeLabel = "Remove" }) => (
  <span onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: color + "14", color, border: `1px solid ${color}2e`, borderRadius: radius.pill, padding: onRemove ? "3px 6px 3px 10px" : "3px 10px", fontSize: font.sm, fontWeight: weight.medium, cursor: onClick ? "pointer" : "default", ...style }}>
    {children}
    {onRemove && (
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label={removeLabel} style={{ display: "flex", background: "none", border: "none", cursor: "pointer", color, padding: 1, borderRadius: "50%", opacity: 0.7 }}
        onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}>
        <Icon name="close" size={13} />
      </button>
    )}
  </span>
);
